/**
 * Route access policy — the ONE declaration of "who may reach this route"
 * (DV-3012, spec §5.1 / D9).
 *
 * This replaces three overlapping declarations that could disagree:
 *   - `PrivateRoute`'s `loginCheck` prop
 *   - `PrivateRoute`'s `orgCheck` prop
 *   - `bootstrapAuth`'s `publicRoutes` array
 *
 * Two orthogonal questions, three states each, because the booleans could not
 * express the third: `orgCheck={false}` asserted "no org may be selected",
 * with no way to say "doesn't care" — which is what blocked DV-3034.
 *
 * Kept apart from `routes.ts` deliberately: that file holds path constants and
 * builders consumed by navigation code, this holds access policy consumed by
 * the guards and the auth bootstrap.
 *
 * ⚠️ **A NEW TOP-LEVEL ROUTE MUST GET A ROW HERE, OR IT SHIPS UNGUARDED —
 * SILENTLY.** The fail-closed default below applies to unmatched paths of two
 * or more segments only. A single-segment unmatched path answers
 * `NOT_FOUND_GUARD` (`any`/`any`), because at this layer an undeclared
 * `/settings` and a mistyped `/setttings` are literally the same string, and
 * BC-4 requires the mistyped one to render the 404 rather than bounce an
 * anonymous visitor through Keycloak. So a `/settings` added to `App.tsx`
 * without a row here gets `any`/`any` from BOTH consumers: `bootstrapAuth`
 * will not demand a token and `PrivateRoute` will assert nothing, with no
 * error, no warning, and a page that looks like it works. A prefix table
 * cannot tell the two cases apart; only this rule can.
 *
 * A key ending in `/*` means "strictly below this path" (DV-3364): it matches
 * every deeper path and never the base itself, so a family's pages can differ
 * from its index. `'/internal/accounts/*'` makes an account's detail pages
 * `any` while the list at `/internal/accounts` stays `none`. Longest key wins
 * as usual; a trailing slash is still stripped first, so `/internal/accounts/`
 * is the list.
 */

import { LEGACY_CONSOLE_PREFIX } from './routes';

/** Whether the route needs a logged-in user. */
export type AuthRequirement =
  /** Must have a token. No token ⇒ capture the deep link and bounce to login. */
  | 'required'
  /** Must NOT have a token (e.g. `/register`). */
  | 'anonymous'
  /** Either way. The route handles its own auth state (`/`, `/callback`). */
  | 'any';

/** Whether the route needs an entity (account) scope. */
export type EntityRequirement =
  /** An entity must be selected; resolve or route to a picker first. */
  | 'required'
  /**
   * Pre-selection surfaces (the org pickers and the admin console) and
   * `/error`, a terminal surface: resolving scope there on a denial would
   * re-read the still-stored `intendedUrl` (BC-2), deny again, reassign
   * `/error?reason=...`, and loop.
   *
   * `'none'` has two different readers, and they disagree: `resolveEntityScope`
   * (`entity-scope-resolution.ts`) reads it as "resolve nothing, don't even
   * look" — the meaning every entry above needs. `PrivateRoute` (spec §5.2)
   * reads it as an assertion instead: `guard.entity === 'required' ? hasOrg
   * : !hasOrg`, i.e. "an org must NOT be selected". `/error` is safe under
   * that assertion today only because it is a bare route in `App.tsx`, never
   * wrapped in `PrivateRoute` — wrapping it later would bounce every
   * org-having user straight from `/error` to `/`.
   */
  | 'none'
  /**
   * Mounts with or without a selected entity. Has no expressible form under
   * the old booleans; this is the value DV-3034's internal asset view needs.
   */
  | 'any';

export interface RouteGuard {
  auth: AuthRequirement;
  entity: EntityRequirement;
}

/**
 * Fail-closed — for an unmatched path of TWO OR MORE segments. Such a path
 * over-guards rather than shipping unguarded: a nested route added later
 * without a table entry becomes a visible bug (bounced to login) instead of a
 * silent hole. A single-segment unmatched path does NOT reach this; see
 * `NOT_FOUND_GUARD` and the module docstring's warning.
 */
const DEFAULT_GUARD: RouteGuard = { auth: 'required', entity: 'required' };

/**
 * Longest-prefix wins, so order here is irrelevant and nested paths inherit.
 * The bootstrap passes a raw `window.location.pathname`
 * (`/home/properties/abc/assets/xyz`), not a route pattern.
 */
const GUARDS: ReadonlyArray<readonly [string, RouteGuard]> = [
  ['/', { auth: 'any', entity: 'any' }],
  ['/callback', { auth: 'any', entity: 'any' }],
  // Terminal surface: entity: 'none' so a denial's still-stored intendedUrl
  // (BC-2, kept so the "Switch Organization" CTA can still land on the deep
  // link) is not re-resolved here — that would deny again, reassign
  // `/error?reason=...`, and loop forever.
  ['/error', { auth: 'any', entity: 'none' }],
  ['/register', { auth: 'anonymous', entity: 'none' }],
  // Public PhotoPlan gallery: anyone with the share link, signed in or not,
  // and never an entity resolution (there is no account context to resolve).
  ['/gallery', { auth: 'any', entity: 'none' }],
  ['/additional-details', { auth: 'required', entity: 'none' }],
  ['/select-organization', { auth: 'required', entity: 'none' }],
  // DV-3520: the console's old prefix. Every /account URL is a redirect to its
  // /internal twin now (LegacyConsoleRedirect, mounted outside PrivateRoute),
  // so this row only has to let the bootstrap through, and only the bootstrap
  // reads it. `none`, not `any`: the bootstrap runs once, against the OLD path,
  // and `none` makes resolveEntityScope return before it looks at scope. A
  // scoped tab's old link still reaches the redirect (nothing asserts `none`
  // on this path), and the destination's own row then decides. `any` would
  // let a `?entityUUID=` in the old URL, or a stale stored intended-URL
  // target, switch an internal admin into that account and land them on
  // /home instead of the console. With no row at all, a deep /account path
  // would fail closed and the bootstrap would resolve an entity for it.
  [LEGACY_CONSOLE_PREFIX, { auth: 'required', entity: 'none' }],
  // The pre-selection internal console. Reachable only before an org is chosen.
  ['/internal', { auth: 'required', entity: 'none' }],
  // DV-3364: an account's detail pages, strictly below the list. `any`: a
  // copied detail link must open in a tab that already has an account, and
  // the route element's UnscopedConsole clears it on entry, as on the asset
  // workspace below. The list itself keeps `none`.
  ['/internal/accounts/*', { auth: 'required', entity: 'any' }],
  // DV-2849: the internal asset workspace opened from Global Orders. `any`,
  // not `none`: it must mount from the unscoped console (its purpose) AND in
  // a tab that happens to be scoped when a copied link is pasted. Longest
  // prefix wins, so `/internal/global-orders` itself stays `none` — the list
  // page is not loosened. No `entityUUID` param is read here, so no account
  // SWITCH happens on this path; the `UnscopedConsole` gate on the route
  // element does un-choose the account on entry (profile-wide, like Switch
  // Organization — spec §3.2), because every other destination inside this
  // console asserts `none` (the account detail pages above are the one other
  // exception) and the page's requests must not carry `X-Entity-UUID` for an
  // account they are not about (PR #1159 review).
  ['/internal/global-orders/assets', { auth: 'required', entity: 'any' }],
  ['/orders', { auth: 'required', entity: 'required' }],
  ['/home', { auth: 'required', entity: 'required' }],
];

/**
 * The NotFound route. Declared, not defaulted — a typo must not demand
 * credentials (BC-4).
 *
 * ⚠️ This is also the ONE fail-open hole in the table, and it is deliberate.
 * It answers every unmatched SINGLE-segment path, so an undeclared top-level
 * app route silently inherits `any`/`any` — see the warning in this module's
 * docstring, and `routeGuard`'s pinning test for the undeclared case.
 */
const NOT_FOUND_GUARD: RouteGuard = { auth: 'any', entity: 'any' };

const isPrefixOf = (prefix: string, path: string): boolean => {
  if (prefix === '/') return path === '/';
  // Segment-aware: '/orders' must not match '/orders-archive'.
  return path === prefix || path.startsWith(`${prefix}/`);
};

const STRICTLY_BELOW = '/*';

/** Whether a table key covers `path`. See the module docstring for `/*` keys. */
const keyCovers = (key: string, path: string): boolean => {
  if (key.endsWith(STRICTLY_BELOW)) {
    const base = key.slice(0, -STRICTLY_BELOW.length);
    return path.startsWith(`${base}/`);
  }
  return isPrefixOf(key, path);
};

/**
 * The path form this table matches against. Query strings are the caller's to
 * strip, but trailing slashes are cheap to tolerate and easy to hit from a
 * hand-typed URL. Every trailing slash goes, not just one: React Router's end
 * match accepts any run of them, so `/internal/accounts//` renders the list and
 * must get the list's row, not the `/*` row below it. Lowercased, because
 * React Router matches routes case-insensitively: `/Gallery/abc` renders the
 * public gallery, so it must get the gallery's row, not the fail-closed
 * default. This is a comparison key only; nothing navigates to it, so path
 * params (UUIDs) keep their case.
 *
 * Exported so code that compares a path to a route LITERAL can agree with the
 * table instead of re-deriving the rule — `bootstrapAuth`'s chunk-load catch
 * exempts `/error`, and `/error/` has to count as the same surface there or
 * the exemption misses and the redirect loops.
 */
export const normalizeRoutePath = (path: string): string => {
  return path.toLowerCase().replace(/\/+$/, '') || '/';
};

export const routeGuard = (path: string): RouteGuard => {
  const normalized = normalizeRoutePath(path);

  let best: readonly [string, RouteGuard] | null = null;
  for (const entry of GUARDS) {
    if (!keyCovers(entry[0], normalized)) continue;
    if (!best || entry[0].length > best[0].length) best = entry;
  }
  if (best) return best[1];

  // Nothing matched. A single-segment unknown path is the NotFound route;
  // anything deeper is treated as an undeclared app route and fails closed.
  return normalized.split('/').filter(Boolean).length <= 1
    ? NOT_FOUND_GUARD
    : DEFAULT_GUARD;
};
